import { environment } from '../environments/environment';
import { DoBootstrap, Injector, NgModule } from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { AppComponent } from './app.component';
import { MapCardComponent } from './components/map-card/map-card.component';
import { ToggleSwitchComponent } from './components/toggle-switch/toggle-switch.component';
import { MapComponent } from './components/map/map.component';
import { MobileMapToggleComponent } from './components/mobile-map-toggle/mobile-map-toggle.component';
import { DesktopMapToggleComponent } from './components/desktop-map-toggle/desktop-map-toggle.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { resultsReducer } from './state/results/results.reducer';
import { MapCardsComponent } from './components/map-cards/map-cards.component';
import { SearchResultListComponent } from './components/search-result-list/search-result-list.component';
import { SortDropdownComponent } from './components/sort-dropdown/sort-dropdown.component';
import { FilterDropdownComponent } from './components/filters/filter-dropdown/filter-dropdown.component';
import { ReactiveFormsModule } from '@angular/forms';
import { DesktopFiltersComponent } from './components/filters/desktop-filters/desktop-filters.component';
import { OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import { CategoryFilterDropdownComponent } from './components/filters/category-filter-dropdown/category-filter-dropdown.component';
import { PriceFilterDropdownComponent } from './components/filters/price-filter-dropdown/price-filter-dropdown.component';
import { RoomCountFilterDropdownComponent } from './components/filters/room-count-filter-dropdown/room-count-filter-dropdown.component';
import { LivingSpaceFilterDropdownComponent } from './components/filters/living-space-filter-dropdown/living-space-filter-dropdown.component';
import { PropertySpaceFilterDropdownComponent } from './components/filters/property-space-filter-dropdown/property-space-filter-dropdown.component';
import { AdditionalFiltersDropdownComponent } from './components/filters/additional-filters-dropdown/additional-filters-dropdown.component';
import { MobileFiltersComponent } from './components/filters/mobile-filters/mobile-filters.component';
import { EffectsModule } from '@ngrx/effects';
import { SimilarObjectsSliderComponent } from './components/similar-objects-slider/similar-objects-slider.component';
import { ChangeFilterCardComponent } from './components/change-filter-card/change-filter-card.component';
import { PlaceholderCardComponent } from './components/placeholder-card/placeholder-card.component';
import { NoResultsComponent } from './components/no-results/no-results.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { RealEstateSearchEffect } from './services/real-estate-search/real-estate-search-effect.service';
import { UsageSpaceFilterDropdownComponent } from './components/filters/usage-space-filter-dropdown/usage-space-filter-dropdown.component';
import { StartupServiceService } from './services/startup-service.service';
import { ObjectSearchLayoutModule } from '@immosky/object-search-layout';
import { ObjectSearchFilterModule } from '@immosky/object-search-filter';
import { AppParametersService, ENV } from '@immosky/types';
import {
  BasicAuthInterceptor,
  DetailPageService,
  PrivacyStateService,
  DevPrivacyStateService,
  SharedModule,
  ProdPrivacyStateService,
} from '@immosky/shared';
import { ObjectSearchAppParametersService } from './services/object-search-app-parameters.service';
import { CardOutletComponent } from './components/card-outlet/card-outlet.component';
import { CardOutletHostDirective } from './components/card-outlet/card-outlet-host.directive';
import { createCustomElement } from '@angular/elements';
import { ObjectSearchDetailPageService } from './services/object-search-detail-page.service';
import { SearchSubscriptionButtonComponent } from './components/search-subscription-button/search-subscription-button.component';
import { SearchSubscriptionOverlayComponent } from './components/search-subscription-overlay/search-subscription-overlay.component';
import { SimilarObjectsSearchEffect } from './services/similar-object-search/similar-objects-search-effect.service';
import { StickyButtonsComponent } from './components/sticky-buttons/sticky-buttons.component';
import { PageLinkComponent } from './components/paginator/page-link/page-link.component';
import { SearchOverlayComponent } from './components/filters/search-overlay/search-overlay.component';
import { MapPrivacyBannerComponent } from './components/map-privacy-banner/map-privacy-banner.component';
import { CardListComponent } from './components/search-result-list/card-list/card-list.component';

@NgModule({
  declarations: [
    AppComponent,
    MapCardComponent,
    ToggleSwitchComponent,
    MapComponent,
    MobileMapToggleComponent,
    DesktopMapToggleComponent,
    PaginatorComponent,
    MapCardsComponent,
    SearchResultListComponent,
    SortDropdownComponent,
    FilterDropdownComponent,
    DesktopFiltersComponent,
    CategoryFilterDropdownComponent,
    PriceFilterDropdownComponent,
    RoomCountFilterDropdownComponent,
    LivingSpaceFilterDropdownComponent,
    UsageSpaceFilterDropdownComponent,
    PropertySpaceFilterDropdownComponent,
    AdditionalFiltersDropdownComponent,
    MobileFiltersComponent,
    SimilarObjectsSliderComponent,
    ChangeFilterCardComponent,
    PlaceholderCardComponent,
    NoResultsComponent,
    CardOutletComponent,
    CardOutletHostDirective,
    SearchSubscriptionButtonComponent,
    SearchSubscriptionOverlayComponent,
    StickyButtonsComponent,
    PageLinkComponent,
    SearchOverlayComponent,
    MapPrivacyBannerComponent,
    CardListComponent,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    OverlayModule,
    HammerModule,
    EffectsModule.forRoot([
      StartupServiceService,
      RealEstateSearchEffect,
      SimilarObjectsSearchEffect,
    ]),
    StoreModule.forRoot(
      {
        results: resultsReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: false,
        },
      }
    ),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
    }),
    A11yModule,
    ObjectSearchLayoutModule,
    ObjectSearchFilterModule,
    SharedModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BasicAuthInterceptor,
      multi: true,
    },
    {
      provide: ENV,
      useValue: environment,
    },
    {
      provide: AppParametersService,
      useClass: ObjectSearchAppParametersService,
    },
    {
      provide: DetailPageService,
      useClass: ObjectSearchDetailPageService,
    },
    {
      provide: PrivacyStateService,
      useFactory: () => {
        if (environment.production) {
          return new ProdPrivacyStateService();
        } else {
          return new DevPrivacyStateService();
        }
      },
    },
  ],
})
export class AppModule implements DoBootstrap {
  constructor(private injector: Injector) {}

  ngDoBootstrap() {
    const customElement = createCustomElement(AppComponent, {
      injector: this.injector,
    });
    customElements.define('object-search', customElement);
  }
}
